import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  X, Mail, MinusCircle, PlusCircle, ArrowLeft,
} from 'lucide-react';
import { InfoTip } from '../components/shared/FormKit';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime } from '../utils/dateFormat';
import SortableHeader, { usePagedSortableData, TablePager } from '../components/SortableTable';
import { fetchJSON } from '../utils/api';

const inputCls = 'px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';

// Previous month as YYYY-MM (the check always looks one month back by default).
const previousMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// '2026-06' -> 'June 2026'
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const monthLabel = (yyyymm) =>
  yyyymm ? `${MONTH_NAMES[parseInt(yyyymm.slice(5, 7), 10) - 1]} ${yyyymm.slice(0, 4)}` : '';

const fmtDateTime = (v) => (v ? formatDateTime(v) : '—');
const fmtDay = (ymd) => {
  if (!ymd) return '';
  const [, m, d] = ymd.split('-');
  return `${parseInt(d, 10)} ${MONTH_NAMES[parseInt(m, 10) - 1].slice(0, 3)}`;
};

const QMTimesheetCheck = () => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('qm:timesheet_ops:edit');

  const [month, setMonth] = useState(previousMonth());
  const [check, setCheck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  // Threshold editing — local value, saved on blur / Enter, then reload.
  const [hoursInput, setHoursInput] = useState('');
  const [savingHours, setSavingHours] = useState(false);

  const [showSend, setShowSend] = useState(false);
  const [showApply, setShowApply] = useState(false);
  const [exempting, setExempting] = useState(null); // row being exempted

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchJSON(`/api/qm/timesheet-check?month=${month}`);
      setCheck(data);
      setHoursInput(String(data.requiredHours));
    } catch (e) { setError(e.message); setCheck(null); }
    finally { setLoading(false); }
  }, [month]);

  useEffect(() => {
    if (!canEdit) return;
    setNotice(null);
    load();
  }, [canEdit, load]);

  const saveHours = async () => {
    const value = parseInt(hoursInput, 10);
    if (!Number.isInteger(value) || value < 1 || value > 744) {
      setError('Required hours must be an integer between 1 and 744');
      return;
    }
    if (check && value === check.requiredHours && check.configured) return;
    setSavingHours(true);
    setError(null);
    try {
      await fetchJSON('/api/qm/timesheet-check/settings', {
        method: 'PUT',
        body: JSON.stringify({ month, requiredHours: value }),
      });
      await load();
    } catch (e) { setError(e.message); }
    finally { setSavingHours(false); }
  };

  const removeExemption = async (row) => {
    setError(null);
    try {
      await fetchJSON(`/api/qm/timesheet-check/exempt/${row.exemption.id}`, { method: 'DELETE' });
      await load();
    } catch (e) { setError(e.message); }
  };

  const rows = useMemo(() => (check ? check.data : []), [check]);
  // Sortable columns + PAGE_SIZE per page, same as every other table.
  const {
    rows: rowPage, sortConfig, requestSort, page, setPage, total,
  } = usePagedSortableData(rows);
  const mailable = useMemo(
    () => rows.filter((r) => !r.exemption && r.email),
    [rows]
  );
  const markTargets = useMemo(() => rows.filter((r) => !r.exemption), [rows]);

  if (!canEdit) {
    return (
      <div className="p-8 max-w-xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-lg font-semibold text-red-800">Editor access required</h2>
          <p className="text-sm text-red-600 mt-1">You need staff record editing rights to access this area.</p>
        </div>
      </div>
    );
  }

  const week = check?.week;

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <Link to="/manage" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="w-4 h-4" />Management</Link>
      <div className="mt-2 mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 inline-flex items-center gap-2">
            Timesheet Check
            <InfoTip text="Uses the data already imported via Chargeable Hours Import." />
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Audit staff whose Adaptive timesheet (work + leave hours) is below the month's required hours.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-slate-500 font-medium mb-1">Month</label>
            <input
              type="month"
              value={month}
              onChange={(e) => e.target.value && setMonth(e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 font-medium mb-1">
              Required Hours{check && !check.configured ? ' (Suggested)' : ''}
            </label>
            <input
              type="number"
              min="1"
              max="744"
              value={hoursInput}
              onChange={(e) => setHoursInput(e.target.value)}
              onBlur={saveHours}
              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
              disabled={savingHours}
              className={`${inputCls} w-28`}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm mb-4">
          <span>{error}</span>
        </div>
      )}
      {notice && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700 text-sm mb-4">
          <span>{notice}</span>
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center text-slate-500">Loading…</div>
      ) : check ? (
        <>
          {!check.hasAdaptiveData && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800 text-sm mb-4">
              <span>
                No Adaptive data has been imported for {monthLabel(month)} yet — every staff member shows 0 hours.
                Download and import it on <Link to="/qm/chargeable-import" className="underline font-medium">Chargeable Hours Import</Link> first.
              </span>
            </div>
          )}
          {check.hasAdaptiveData && check.pendingNames > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800 text-sm mb-4">
              <span>
                {check.pendingNames} Adaptive name(s) for this month are still unmatched/unimported — their staff may
                wrongly appear below. Resolve them on <Link to="/qm/chargeable-import" className="underline font-medium">Chargeable Hours Import</Link>.
              </span>
            </div>
          )}

          {/* Status + actions */}
          <div className="bg-white rounded-lg border border-slate-200 p-4 mb-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-slate-600">
                <span>
                  {week.weekNo === 0
                    ? `${monthLabel(month)} has not ended yet`
                    : `Check week ${week.weekNo} of ${monthLabel(month) && MONTH_NAMES[(parseInt(month.slice(5, 7), 10)) % 12]}`}
                  {week.weekNo === 1 && ' — grace week, reminders only'}
                </span>
                <span>
                  Last import: {fmtDateTime(check.lastImportAt)}
                </span>
                {week.applied && (
                  <span className="text-green-700">
                    Marks applied this week ({fmtDateTime(week.applied.appliedAt)})
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowSend(true)}
                  disabled={mailable.length === 0}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Mail className="w-4 h-4" /> Send Reminders
                </button>
                <button
                  onClick={() => setShowApply(true)}
                  disabled={!week.marksAllowed || markTargets.length === 0}
                  title={week.weekNo < 2
                    ? 'Marks can be applied from week 2 of the following month'
                    : week.applied ? 'Already applied this week' : ''}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PlusCircle className="w-4 h-4" /> Apply Marks (+1)
                </button>
              </div>
            </div>
          </div>

          {/* Defaulter table */}
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="table-scroll">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    {[
                      { label: 'Name', key: 'name' },
                      { label: 'Work Hours', key: 'workHours' },
                      { label: 'Leave Hours', key: 'leaveHours' },
                      { label: `Total (of ${check.requiredHours})`, key: 'totalHours' },
                      { label: 'Current Marks', key: 'currentMarks' },
                      { label: 'Notes', key: 'notes', sortable: false },
                      { label: 'Email', key: 'email' },
                      { label: 'Exempt', key: 'exempt', accessor: (r) => (r.exemption ? 1 : 0) },
                    ].map((c) => (
                      <SortableHeader
                        key={c.key}
                        label={c.label}
                        sortKey={c.key}
                        accessor={c.accessor}
                        sortable={c.sortable !== false}
                        sortConfig={sortConfig}
                        onSort={requestSort}
                        className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap"
                      />
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                        Everyone has met the required hours for {monthLabel(month)}.
                      </td>
                    </tr>
                  ) : rowPage.map((r) => (
                    <tr key={r.staffId} className={r.exemption ? 'bg-slate-50 text-slate-400' : 'hover:bg-slate-50'}>
                      <td className="px-4 py-2 font-medium whitespace-nowrap">{r.name}</td>
                      <td className="px-4 py-2">{r.workHours}</td>
                      <td className="px-4 py-2">{r.leaveHours}</td>
                      <td className="px-4 py-2 font-semibold">{r.totalHours}</td>
                      <td className="px-4 py-2">{r.currentMarks}</td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {r.joined && <span className="inline-block mr-2 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs">Joined {fmtDay(r.joined)}</span>}
                        {r.left && <span className="inline-block px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 text-xs">Left {fmtDay(r.left)}</span>}
                        {r.exemption && <span className="inline-block px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 text-xs" title={r.exemption.reason || ''}>Exempted{r.exemption.reason ? `: ${r.exemption.reason}` : ''}</span>}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">{r.email || <span className="text-red-500">no email</span>}</td>
                      <td className="px-4 py-2">
                        {r.exemption ? (
                          <button onClick={() => removeExemption(r)} className="text-xs text-blue-600 hover:text-blue-800">Undo</button>
                        ) : (
                          <button onClick={() => setExempting(r)} title="Exempt this month" className="p-1.5 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded">
                            <MinusCircle className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <TablePager
              page={page}
              setPage={setPage}
              total={total}
              className="flex items-center justify-between px-4 py-2 border-t border-slate-200 bg-slate-50"
            />
            <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 text-sm text-slate-500">
              {rows.length} below threshold ・ {markTargets.length} counted (excl. exempted) ・ {mailable.length} reachable by email
            </div>
          </div>

          {showSend && (
            <SendRemindersModal
              month={month}
              rows={mailable}
              onClose={() => setShowSend(false)}
              onSent={(result) => {
                setShowSend(false);
                setNotice(`Reminders sent: ${result.sent}${result.skipped.length ? `, skipped: ${result.skipped.length}` : ''}`);
                load();
              }}
            />
          )}
          {showApply && (
            <ApplyMarksModal
              month={month}
              targets={markTargets}
              onClose={() => setShowApply(false)}
              onApplied={(result) => {
                setShowApply(false);
                setNotice(`Marks applied to ${result.applied} staff (week ${result.weekKey})`);
                load();
              }}
            />
          )}
          {exempting && (
            <ExemptModal
              month={month}
              row={exempting}
              onClose={() => setExempting(null)}
              onSaved={() => { setExempting(null); load(); }}
            />
          )}
        </>
      ) : null}
    </div>
  );
};

// Preview + confirm bulk reminder emails. Recipients are pre-ticked; the
// preview pane shows the exact HTML of the highlighted recipient's mail.
const SendRemindersModal = ({ month, rows, onClose, onSent }) => {
  const [selected, setSelected] = useState(() => new Set(rows.map((r) => r.staffId)));
  const [previewFor, setPreviewFor] = useState(rows[0]?.staffId ?? null);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (previewFor === null) return;
    let stale = false;
    setPreview(null);
    fetchJSON(`/api/qm/timesheet-check/reminder/render?month=${month}&staffId=${previewFor}`)
      .then((d) => { if (!stale) setPreview(d); })
      .catch((e) => { if (!stale) setError(e.message); });
    return () => { stale = true; };
  }, [month, previewFor]);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const send = async () => {
    setSending(true);
    setError(null);
    try {
      const result = await fetchJSON('/api/qm/timesheet-check/reminder/send', {
        method: 'POST',
        body: JSON.stringify({ month, staffIds: [...selected] }),
      });
      onSent(result);
    } catch (e) { setError(e.message); setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">Send Timesheet Reminders — {monthLabel(month)}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4 overflow-y-auto">
          <div className="border border-slate-200 rounded-lg overflow-y-auto max-h-[50vh]">
            {rows.map((r) => (
              <label key={r.staffId} className={`flex items-center gap-2 px-3 py-2 border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${previewFor === r.staffId ? 'bg-blue-50' : ''}`}>
                <input type="checkbox" checked={selected.has(r.staffId)} onChange={() => toggle(r.staffId)} />
                <button type="button" onClick={() => setPreviewFor(r.staffId)} className="flex-1 text-left">
                  <span className="text-sm text-slate-800">{r.name}</span>
                  <span className="block text-xs text-slate-500">{r.email} ・ {r.totalHours} hrs</span>
                </button>
              </label>
            ))}
          </div>
          <div className="border border-slate-200 rounded-lg overflow-hidden flex flex-col">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs text-slate-500">
              {preview ? <>Preview — <span className="font-medium">{preview.subject}</span></> : 'Loading preview…'}
            </div>
            {preview && (
              <iframe title="Reminder preview" srcDoc={preview.html} className="flex-1 min-h-[40vh] bg-white" />
            )}
          </div>
        </div>
        <div className="p-5 border-t border-slate-200">
          {error && (
            <div className="p-3 mb-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <span>{error}</span>
            </div>
          )}
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-500">{selected.size} of {rows.length} recipients selected</span>
            <div className="flex gap-2">
              <button onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
              <button
                onClick={send}
                disabled={sending || selected.size === 0}
                className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {sending ? 'Sending…' : `Send ${selected.size} Reminder${selected.size === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ApplyMarksModal = ({ month, targets, onClose, onApplied }) => {
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);

  const apply = async () => {
    setApplying(true);
    setError(null);
    try {
      const result = await fetchJSON('/api/qm/timesheet-check/apply-marks', {
        method: 'POST',
        body: JSON.stringify({ month }),
      });
      onApplied(result);
    } catch (e) { setError(e.message); setApplying(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">Apply Weekly Marks</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-700">
            Deduct <span className="font-semibold">1 mark</span> from each of the{' '}
            <span className="font-semibold">{targets.length}</span> non-exempt staff below the required hours
            for <span className="font-semibold">{monthLabel(month)}</span>?
          </p>
          <p className="text-xs text-slate-500">
            This can be done once per calendar week. Marks land in Monthly Records (timesheet) and can be adjusted there.
          </p>
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <span>{error}</span>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
            <button onClick={apply} disabled={applying} className="px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50">
              {applying ? 'Applying…' : 'Apply Marks'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ExemptModal = ({ month, row, onClose, onSaved }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await fetchJSON('/api/qm/timesheet-check/exempt', {
        method: 'POST',
        body: JSON.stringify({ month, staffId: row.staffId, reason: reason.trim() || null }),
      });
      onSaved();
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">Exempt for {monthLabel(month)}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-700">
            <span className="font-semibold">{row.name}</span> will get no reminder and no marks for this month.
            Marks already applied are not reverted — adjust those in Monthly Records if needed.
          </p>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Reason</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. full-month leave, joined mid-month"
              className={`${inputCls} w-full`}
            />
          </div>
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <span>{error}</span>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
            <button onClick={save} disabled={saving} className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Exempt'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QMTimesheetCheck;
